import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Target,
  TrendingUp,
  Users,
  MousePointerClick,
  Search,
  Lightbulb,
  RefreshCw,
  BarChart3,
  Rocket,
  Megaphone,
  ChartNoAxesCombined,
  CircleDollarSign,
  Sparkles,
  ShieldCheck,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const C = {
  bg: "#080d13",
  panel: "rgba(17, 25, 35, 0.84)",
  border: "rgba(163, 230, 53, 0.18)",
  green: "#a3e635",
  greenSoft: "rgba(163, 230, 53, 0.10)",
  text: "#edf2f7",
  muted: "#a2adba",
  faint: "#6f7c8b",
};

const services = [
  {
    icon: Megaphone,
    title: "Paid Advertising",
    description:
      "Create and manage high-performance campaigns across major advertising platforms to reach the right audience at the right time.",
  },
  {
    icon: Target,
    title: "Lead Generation",
    description:
      "Build campaigns focused on generating qualified leads for your sales team and business.",
  },
  {
    icon: MousePointerClick,
    title: "Conversion Optimization",
    description:
      "Analyze user behavior and optimize campaigns, landing pages, creatives, and targeting to improve conversion rates.",
  },
  {
    icon: Search,
    title: "Audience & Market Research",
    description:
      "Identify ideal customers, market segments, competitors, and opportunities before scaling your campaigns.",
  },
  {
    icon: Lightbulb,
    title: "Creative Strategy",
    description:
      "Develop ad concepts, messaging, creatives, and campaign angles designed to capture attention and drive action.",
  },
  {
    icon: RefreshCw,
    title: "Campaign Optimization",
    description:
      "Monitor performance and optimize budgets, audiences, placements, creatives, and bidding strategies.",
  },
  {
    icon: BarChart3,
    title: "Analytics & Reporting",
    description:
      "Track key performance indicators and report on campaign performance, conversions, acquisition costs, and return on investment.",
  },
];

const process = [
  {
    number: "01",
    title: "Research & Strategy",
    description:
      "Understand your business, market, competitors, customers, and growth objectives.",
  },
  {
    number: "02",
    title: "Campaign Setup",
    description:
      "Build your campaign structure, audiences, tracking, creatives, and conversion funnels.",
  },
  {
    number: "03",
    title: "Launch & Testing",
    description:
      "Launch campaigns and test different audiences, creatives, offers, and messaging.",
  },
  {
    number: "04",
    title: "Optimize & Scale",
    description:
      "Identify winning campaigns, reduce wasted spend, improve performance, and scale what works.",
  },
  {
    number: "05",
    title: "Report & Improve",
    description:
      "Measure results, identify opportunities, and continuously improve campaign performance.",
  },
];

const PerformanceMarketingPricing = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".pm-hero-content > *",
        { opacity: 0, y: 24 },
        { opacity: 1, y: 0, duration: 0.75, stagger: 0.1, ease: "power3.out" }
      );

      gsap.utils
        .toArray(".pm-reveal")
        .forEach((element) => {
          gsap.fromTo(
            element,
            { opacity: 0, y: 26 },
            {
              opacity: 1,
              y: 0,
              duration: 0.65,
              ease: "power2.out",
              scrollTrigger: {
                trigger: element,
                start: "top 88%",
                once: true,
              },
            }
          );
        });

      gsap.to(".pm-orb-one", {
        y: -25,
        x: 15,
        duration: 5,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
      gsap.to(".pm-orb-two", {
        y: 22,
        x: -12,
        duration: 6,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  const styles = {
    page: {
      position: "relative",
      overflow: "hidden",
      isolation: "isolate",
      minHeight: "100vh",
      background: C.bg,
      color: C.text,
      padding: "0 24px 80px",
      fontFamily:
        "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    },
    background: {
      position: "absolute",
      inset: 0,
      zIndex: -1,
      pointerEvents: "none",
      backgroundImage:
        "linear-gradient(rgba(163,230,53,.035) 1px, transparent 1px), linear-gradient(90deg, rgba(163,230,53,.035) 1px, transparent 1px)",
      backgroundSize: "58px 58px",
      maskImage: "linear-gradient(to bottom, black, transparent 75%)",
    },
    orb: {
      position: "absolute",
      width: 310,
      height: 310,
      borderRadius: "50%",
      filter: "blur(90px)",
      background: C.green,
      opacity: 0.12,
    },
    container: { width: "100%", maxWidth: 1180, margin: "0 auto" },
    hero: {
      minHeight: 590,
      padding: "100px 0 76px",
      display: "grid",
      gridTemplateColumns: "1.08fr .92fr",
      gap: 55,
      alignItems: "center",
    },
    eyebrow: {
      display: "inline-flex",
      alignItems: "center",
      gap: 9,
      color: C.green,
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: "0.17em",
      marginBottom: 24,
    },
    h1: {
      margin: "0 0 22px",
      fontSize: "clamp(42px, 6vw, 76px)",
      fontWeight: 850,
      letterSpacing: "-0.06em",
      lineHeight: 1.02,
    },
    lead: {
      color: C.muted,
      maxWidth: 650,
      fontSize: 16,
      lineHeight: 1.85,
      margin: "0 0 28px",
    },
    actions: { display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" },
    primaryButton: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      padding: "14px 19px",
      borderRadius: 10,
      background: C.green,
      color: "#10170a",
      textDecoration: "none",
      fontSize: 13,
      fontWeight: 800,
      boxShadow: "0 8px 30px rgba(163,230,53,.15)",
      transition: "transform .2s ease, box-shadow .2s ease",
    },
    secondaryButton: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 9,
      padding: "13px 17px",
      borderRadius: 10,
      border: `1px solid ${C.border}`,
      color: C.text,
      textDecoration: "none",
      fontSize: 13,
      fontWeight: 700,
      background: "rgba(255,255,255,.02)",
    },
    heroVisual: {
      position: "relative",
      minHeight: 410,
      display: "grid",
      placeItems: "center",
    },
    visualPanel: {
      width: "min(100%, 420px)",
      padding: 25,
      borderRadius: 25,
      border: `1px solid ${C.border}`,
      background:
        "linear-gradient(145deg, rgba(163,230,53,.08), rgba(17,25,35,.94) 42%)",
      boxShadow: "0 28px 90px rgba(0,0,0,.3)",
      backdropFilter: "blur(18px)",
      transform: "rotate(-2deg)",
    },
    visualTop: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
      marginBottom: 25,
    },
    tinyLabel: {
      color: C.faint,
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: ".13em",
    },
    status: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      color: C.green,
      background: C.greenSoft,
      border: `1px solid ${C.border}`,
      borderRadius: 99,
      padding: "6px 9px",
      fontSize: 10,
      fontWeight: 800,
    },
    visualMetric: {
      padding: "19px 0",
      borderTop: `1px solid ${C.border}`,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 12,
    },
    section: { maxWidth: 1180, margin: "0 auto", paddingTop: 70 },
    sectionHeader: { maxWidth: 720, marginBottom: 34 },
    kicker: {
      color: C.green,
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: ".17em",
      textTransform: "uppercase",
      display: "block",
      marginBottom: 13,
    },
    h2: {
      margin: "0 0 14px",
      fontSize: "clamp(28px, 4vw, 44px)",
      lineHeight: 1.12,
      letterSpacing: "-.045em",
    },
    sectionLead: { margin: 0, color: C.muted, fontSize: 15, lineHeight: 1.8 },
    investmentGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 18,
    },
    investmentCard: {
      position: "relative",
      padding: "32px",
      borderRadius: 22,
      border: `1px solid rgba(163,230,53,.35)`,
      background:
        "linear-gradient(135deg, rgba(163,230,53,.11), rgba(17,25,35,.92) 55%)",
      overflow: "hidden",
    },
    priceLabel: {
      color: C.muted,
      fontSize: 12,
      fontWeight: 700,
      display: "block",
      marginBottom: 13,
    },
    price: {
      fontSize: "clamp(34px, 4.6vw, 56px)",
      fontWeight: 850,
      letterSpacing: "-.055em",
      lineHeight: 1.05,
      marginBottom: 14,
    },
    note: { color: C.muted, fontSize: 13, lineHeight: 1.8, margin: 0 },
    miniList: { display: "grid", gap: 13, marginTop: 24 },
    miniItem: { display: "flex", gap: 10, alignItems: "flex-start", color: C.muted, fontSize: 13, lineHeight: 1.6 },
    serviceGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: 15,
    },
    serviceCard: {
      padding: 23,
      borderRadius: 17,
      background: C.panel,
      border: `1px solid ${C.border}`,
      transition: "transform .22s ease, border-color .22s ease, background .22s ease",
    },
    iconBox: {
      width: 43,
      height: 43,
      borderRadius: 12,
      display: "grid",
      placeItems: "center",
      color: C.green,
      background: C.greenSoft,
      border: `1px solid ${C.border}`,
      marginBottom: 19,
    },
    serviceTitle: { fontSize: 16, margin: "0 0 10px", letterSpacing: "-.02em" },
    serviceText: { color: C.muted, fontSize: 13, lineHeight: 1.8, margin: 0 },
    processGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
      gap: 12,
    },
    processCard: {
      padding: "21px 17px",
      borderTop: `2px solid ${C.green}`,
      borderRadius: "0 0 14px 14px",
      background: "linear-gradient(180deg, rgba(163,230,53,.07), rgba(17,25,35,.72))",
      borderLeft: `1px solid ${C.border}`,
      borderRight: `1px solid ${C.border}`,
      borderBottom: `1px solid ${C.border}`,
    },
    processNumber: { color: C.green, fontSize: 11, fontWeight: 900, letterSpacing: ".12em", display: "block", marginBottom: 21 },
    processTitle: { fontSize: 14, margin: "0 0 10px", lineHeight: 1.45 },
    processText: { color: C.muted, fontSize: 12, lineHeight: 1.75, margin: 0 },
    closing: {
      maxWidth: 1180,
      margin: "85px auto 0",
      padding: "42px",
      display: "grid",
      gridTemplateColumns: "1fr auto",
      alignItems: "center",
      gap: 28,
      borderRadius: 24,
      border: `1px solid rgba(163,230,53,.3)`,
      background: "linear-gradient(115deg, rgba(163,230,53,.12), rgba(17,25,35,.94) 58%)",
    },
  };

  return (
    <main className="performance-pricing-page" ref={pageRef} style={styles.page}>
      <style>{`
        .performance-pricing-page * { box-sizing: border-box; }
        .performance-pricing-page a:focus-visible { outline: 2px solid ${C.green}; outline-offset: 4px; }
        .pm-primary:hover { transform: translateY(-2px); box-shadow: 0 12px 34px rgba(163,230,53,.25) !important; }
        .pm-secondary:hover { border-color: rgba(163,230,53,.55) !important; color: ${C.green} !important; }
        .pm-service-card:hover { transform: translateY(-4px); border-color: rgba(163,230,53,.42) !important; background: rgba(20,30,39,.96) !important; }
        .pm-process-card { min-width: 0; }
        .pm-dot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; background: ${C.green}; box-shadow: 0 0 12px rgba(163,230,53,.75); }
        @media (max-width: 1000px) {
          .pm-hero { grid-template-columns: 1fr !important; padding-top: 75px !important; min-height: auto !important; }
          .pm-hero-visual { min-height: 330px !important; }
          .pm-service-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .pm-process-grid { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
        }
        @media (max-width: 650px) {
          .performance-pricing-page { padding: 0 16px 50px !important; }
          .pm-hero { padding: 60px 0 30px !important; gap: 24px !important; }
          .pm-hero-visual { min-height: 300px !important; }
          .pm-section { padding-top: 48px !important; }
          .pm-investment-grid, .pm-service-grid, .pm-process-grid { grid-template-columns: 1fr !important; }
          .pm-investment-card { padding: 25px !important; }
          .pm-service-card { padding: 21px !important; }
          .pm-closing { grid-template-columns: 1fr !important; padding: 28px 23px !important; margin-top: 55px !important; }
          .pm-price { font-size: 42px !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          .performance-pricing-page * { scroll-behavior: auto !important; transition: none !important; }
        }
      `}</style>

      <div aria-hidden="true" style={styles.background}>
        <div className="pm-orb-one" style={{ ...styles.orb, top: 80, left: -150 }} />
        <div className="pm-orb-two" style={{ ...styles.orb, top: 650, right: -150, width: 260, height: 260, opacity: 0.08 }} />
      </div>

      <div style={styles.container}>
        <section className="pm-hero" style={styles.hero}>
          <div className="pm-hero-content">
            <div style={styles.eyebrow}><span className="pm-dot" /> PERFORMANCE MARKETING</div>
            <h1 style={styles.h1}>
              Turn Ad Spend Into <span style={{ color: C.green, textShadow: "0 0 34px rgba(163,230,53,.13)" }}>Measurable Growth.</span>
            </h1>
            <p style={styles.lead}>
              We build and manage performance marketing campaigns designed to generate qualified leads, sales, conversions, and measurable business growth.
            </p>
            <p style={{ ...styles.lead, marginTop: -12 }}>
              From strategy and audience research to campaign execution, optimization, and reporting, we manage the complete performance marketing process with a focus on ROI and sustainable results.
            </p>
            <div style={styles.actions}>
              <a className="pm-primary" href="mailto:YOUR_BUSINESS_EMAIL?subject=Performance%20Marketing%20Consultation" style={styles.primaryButton}>
                Discuss Your Campaign <ArrowRight size={16} />
              </a>
              <a className="pm-secondary" href="#investment" style={styles.secondaryButton}>
                View Investment <ArrowUpRight size={15} />
              </a>
            </div>
          </div>

          <div className="pm-hero-visual" style={styles.heroVisual} aria-hidden="true">
            <div style={styles.visualPanel}>
              <div style={styles.visualTop}>
                <div>
                  <span style={styles.tinyLabel}>CAMPAIGN OVERVIEW</span>
                  <h3 style={{ margin: "7px 0 0", fontSize: 19, letterSpacing: "-.03em" }}>Performance snapshot</h3>
                </div>
                <span style={styles.status}><span className="pm-dot" /> OPTIMIZATION</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 23 }}>
                <div style={{ width: 48, height: 48, display: "grid", placeItems: "center", borderRadius: 14, background: C.greenSoft, color: C.green, border: `1px solid ${C.border}` }}>
                  <ChartNoAxesCombined size={25} />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: C.muted }}>Campaign decisions</div>
                  <div style={{ fontSize: 17, fontWeight: 800, marginTop: 4 }}>Driven by data</div>
                </div>
              </div>
              {[
                ["Strategy", "Audience + offer"],
                ["Optimization", "Test and refine"],
                ["Measurement", "Track conversions"],
              ].map(([label, value]) => (
                <div key={label} style={styles.visualMetric}>
                  <span style={{ color: C.muted, fontSize: 12 }}>{label}</span>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>{value}</span>
                </div>
              ))}
              <div style={{ display: "flex", gap: 5, alignItems: "end", height: 55, marginTop: 22 }}>
                {[20, 29, 25, 38, 34, 45, 40, 53, 48, 55, 50, 63, 59, 72, 67, 82].map((height, index) => (
                  <div key={index} style={{ flex: 1, height: `${height}%`, borderRadius: "4px 4px 1px 1px", background: `rgba(163,230,53,${0.25 + (index / 16) * 0.65})` }} />
                ))}
              </div>
              <p style={{ color: C.faint, fontSize: 10, margin: "10px 0 0" }}>Illustrative visual — not actual campaign data or a performance guarantee.</p>
            </div>
            <div style={{ ...styles.floatingCard, top: 12, right: -6 }}><Target size={16} color={C.green} /><span>Outcome focused</span></div>
            <div style={{ ...styles.floatingCard, bottom: 8, left: -8 }}><TrendingUp size={16} color={C.green} /><span>Test. Learn. Scale.</span></div>
          </div>
        </section>
      </div>

      <section id="investment" className="pm-section pm-reveal" style={styles.section}>
        <div style={styles.sectionHeader}>
          <span style={styles.kicker}>CAMPAIGN INVESTMENT</span>
          <h2 style={styles.h2}>A budget built around your growth goals.</h2>
          <p style={styles.sectionLead}>
            Campaign investment is customized based on your business goals, target market, advertising platforms, campaign scale, and expected growth objectives.
          </p>
        </div>
        <div className="pm-investment-grid" style={styles.investmentGrid}>
          <div className="pm-investment-card" style={styles.investmentCard}>
            <span style={styles.priceLabel}>PERFORMANCE MARKETING INVESTMENT</span>
            <div className="pm-price" style={styles.price}>$1,200 <span style={{ color: C.muted, fontSize: 20, fontWeight: 600 }}>– $8,000+</span></div>
            <p style={styles.note}>
              Starting from $1,200, with larger engagements priced according to campaign scope, complexity, and growth objectives.
            </p>
            <div style={styles.miniList}>
              {[
                "Strategy, audience research, and campaign planning",
                "Campaign setup, execution, and ongoing optimization",
                "Performance tracking and reporting",
                "Scope tailored to your business objectives",
              ].map((item) => (
                <div key={item} style={styles.miniItem}><Check size={16} color={C.green} style={{ flexShrink: 0, marginTop: 2 }} /><span>{item}</span></div>
              ))}
            </div>
            <a className="pm-primary" href="mailto:YOUR_BUSINESS_EMAIL?subject=Performance%20Marketing%20Pricing" style={{ ...styles.primaryButton, marginTop: 27 }}>
              Request a Custom Quote <ArrowRight size={16} />
            </a>
          </div>
          <div style={{ ...styles.card, padding: 30, borderRadius: 22, border: `1px solid ${C.border}`, background: C.panel }}>
            <div style={styles.iconBox}><CircleDollarSign size={22} /></div>
            <h3 style={{ fontSize: 20, margin: "0 0 12px", letterSpacing: "-.03em" }}>What shapes the investment?</h3>
            <p style={{ ...styles.note, marginBottom: 23 }}>
              Final investment depends on campaign objectives, geographic targeting, advertising platforms, campaign complexity, and required services.
            </p>
            {[
              ["Business goals", "Lead generation, sales, launches, or market expansion"],
              ["Campaign scope", "Number of platforms, campaigns, and audiences"],
              ["Creative needs", "Ad concepts, messaging, and creative production"],
              ["Measurement", "Tracking setup, analytics, and reporting requirements"],
            ].map(([title, description]) => (
              <div key={title} style={{ display: "grid", gridTemplateColumns: "10px 1fr", gap: 12, padding: "15px 0", borderTop: `1px solid ${C.border}` }}>
                <span className="pm-dot" style={{ marginTop: 7 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 5 }}>{title}</div>
                  <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.7 }}>{description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pm-section pm-reveal" style={styles.section}>
        <div style={styles.sectionHeader}>
          <span style={styles.kicker}>WHAT WE DO</span>
          <h2 style={styles.h2}>Every part of the performance process.</h2>
          <p style={styles.sectionLead}>
            A connected approach to campaign strategy, execution, measurement, and continuous improvement.
          </p>
        </div>
        <div className="pm-service-grid" style={styles.serviceGrid}>
          {services.map((service) => {
            const Icon = service.icon;
            return (
              <article className="pm-service-card" key={service.title} style={styles.serviceCard}>
                <div style={styles.iconBox}><Icon size={21} /></div>
                <h3 style={styles.serviceTitle}>{service.title}</h3>
                <p style={styles.serviceText}>{service.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="pm-section pm-reveal" style={styles.section}>
        <div style={styles.sectionHeader}>
          <span style={styles.kicker}>OUR PROCESS</span>
          <h2 style={styles.h2}>From first insight to continuous improvement.</h2>
          <p style={styles.sectionLead}>
            A structured process keeps campaigns focused, measurable, and ready to adapt as we learn.
          </p>
        </div>
        <div className="pm-process-grid" style={styles.processGrid}>
          {process.map((step) => (
            <article className="pm-process-card" key={step.number} style={styles.processCard}>
              <span style={styles.processNumber}>{step.number} <span style={{ color: C.faint }}>—</span></span>
              <h3 style={styles.processTitle}>{step.title}</h3>
              <p style={styles.processText}>{step.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="pm-closing pm-reveal" style={styles.closing}>
        <div>
          <span style={styles.kicker}>BUILT AROUND YOUR GROWTH GOALS</span>
          <h2 style={{ ...styles.h2, maxWidth: 700, marginBottom: 13 }}>Let's build a campaign that performs.</h2>
          <p style={{ ...styles.sectionLead, maxWidth: 690 }}>
            Whether you're looking to generate leads, increase online sales, launch a new product, or expand into a new market, our campaigns are designed around measurable business outcomes.
          </p>
        </div>
        <a className="pm-primary" href="mailto:YOUR_BUSINESS_EMAIL?subject=Let's%20Build%20a%20Performance%20Marketing%20Campaign" style={styles.primaryButton}>
          Let's Talk <ArrowRight size={16} />
        </a>
      </section>

      <div style={{ ...styles.container, paddingTop: 26 }}>
        <p style={{ color: C.faint, fontSize: 11, lineHeight: 1.7, margin: 0 }}>
          Investment is customized for each engagement. Advertising spend and third-party platform costs should be confirmed in your proposal and may be separate from service fees. Campaign outcomes depend on multiple factors and are not guaranteed.
        </p>
      </div>
    </main>
  );
};

export default PerformanceMarketingPricing;
